import { Form, Formik } from "formik";
import {
  CreateTechnicianDto,
  PatchTechnicianDto,
  Technician,
} from "@/models/Technician.ts";
import * as yup from "yup";
import { FormikInput } from "@/components/common/form/FormikInput.tsx";
import { FormikSelect } from "@/components/common/form/FomikSelect.tsx";
import { Button } from "@/components/base/Button.tsx";
import { PhoneType } from "@/types/Phone.ts";

type CreateEditTechnicianFormProps = {
  onSubmit: (values: CreateTechnicianDto | PatchTechnicianDto) => Promise<void>;
  initialTechnicianData?: Technician;
  onCancel?: () => void;
  tenantId: string;
  isEditable?: boolean;
};

const validationSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
});

export const CreateEditTechnicianForm = ({
  initialTechnicianData,
  onCancel,
  onSubmit,
  tenantId,
  isEditable,
}: CreateEditTechnicianFormProps) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  return (
    <Formik
      initialValues={{
        firstName: initialTechnicianData?.firstName ?? "",
        lastName: initialTechnicianData?.lastName ?? "",
        email: initialTechnicianData?.email ?? "",
        phoneNumber: initialTechnicianData?.primaryPhone?.phoneNumber ?? "",
        phoneNumberType: initialTechnicianData?.primaryPhone?.type ?? "",
        homeAddressStreet: initialTechnicianData?.homeAddress?.address1 ?? "",
        homeAddressStreet2: initialTechnicianData?.homeAddress?.address2 ?? "",
        homeAddressCity: initialTechnicianData?.homeAddress?.city ?? "",
        homeAddressZip: initialTechnicianData?.homeAddress?.zip ?? "",
        homeAddressState: initialTechnicianData?.homeAddress?.state ?? "",
        workAddressStreet: initialTechnicianData?.workAddress?.address1 ?? "",
        workAddressStreet2: initialTechnicianData?.workAddress?.address2 ?? "",
        workAddressCity: initialTechnicianData?.workAddress?.city ?? "",
        workAddressZip: initialTechnicianData?.workAddress?.zip ?? "",
        workAddressState: initialTechnicianData?.workAddress?.state ?? "",
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        let submitData: CreateTechnicianDto | PatchTechnicianDto = {
          tenantId: tenantId,
          firstName: values.firstName,
          lastName: values.lastName,
          displayName: `${values.firstName} ${values.lastName}`,
          email: values.email,
          primaryPhone: {
            phoneNumber: values.phoneNumber,
            type: values.phoneNumberType as PhoneType,
          },
          homeAddress: {
            address1: values.homeAddressStreet,
            address2: values.homeAddressStreet2,
            city: values.homeAddressCity,
            state: values.homeAddressState,
            zip: values.homeAddressZip,
          },
          workAddress: {
            address1: values.workAddressStreet,
            address2: values.workAddressStreet2,
            city: values.workAddressCity,
            state: values.workAddressState,
            zip: values.workAddressZip,
          },
          role: "user",
        };
        if (initialTechnicianData?.id) {
          submitData = {
            id: initialTechnicianData.id,
            ...submitData,
          } as PatchTechnicianDto;
        }

        onSubmit(submitData).finally(() => setSubmitting(false));
        return;
      }}
    >
      {({ handleSubmit }) => (
        <Form
          className="flex flex-col sm:grid sm:grid-cols-3 gap-4 m-auto w-64 sm:w-auto max-w-screen-md"
          onSubmit={handleSubmit}
        >
          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Technician Information</h3>
            <FormikInput
              name="firstName"
              type="text"
              id="firstName"
              label="First Name"
              disabled={!isEditable}
            />
            <FormikInput
              name="lastName"
              type="text"
              id="lastName"
              label="Last Name"
              disabled={!isEditable}
            />
            <FormikInput
              name="email"
              type="text"
              id="email"
              label="Email"
              disabled={!isEditable}
            />
            <FormikInput
              name="phoneNumber"
              type="text"
              id="phoneNumber"
              label="Phone Number"
              disabled={!isEditable}
            />
            <FormikSelect
              name="phoneNumberType"
              id="phoneNumberType"
              label="Phone Type"
              options={[
                { value: "mobile", label: "Mobile" },
                { value: "work", label: "Work" },
                { value: "home", label: "Home" },
                { value: "other", label: "Other" },
              ]}
              disabled={!isEditable}
            />
          </div>

          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Home Address</h3>

            <FormikInput
              name="homeAddressStreet"
              type="text"
              id="homeAddressStreet"
              label="Street Address"
              disabled={!isEditable}
            />
            <FormikInput
              name="homeAddressStreet2"
              type="text"
              id="homeAddressStreet2"
              label="Street Address 2"
              disabled={!isEditable}
            />
            <FormikInput
              name="homeAddressCity"
              type="text"
              id="homeAddressCity"
              label="City"
              disabled={!isEditable}
            />
            <FormikInput
              name="homeAddressState"
              type="text"
              id="homeAddressState"
              label="State"
              disabled={!isEditable}
            />
            <FormikInput
              name="homeAddressZip"
              type="text"
              id="homeAddressZip"
              label="Zip"
              disabled={!isEditable}
            />
          </div>

          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Work Address</h3>

            <FormikInput
              name="workAddressStreet"
              type="text"
              id="workAddressStreet"
              label="Street Address"
              disabled={!isEditable}
            />
            <FormikInput
              name="workAddressStreet2"
              type="text"
              id="workAddressStreet2"
              label="Street Address 2"
              disabled={!isEditable}
            />
            <FormikInput
              name="workAddressCity"
              type="text"
              id="workAddressCity"
              label="City"
              disabled={!isEditable}
            />
            <FormikInput
              name="workAddressState"
              type="text"
              id="workAddressState"
              label="State"
              disabled={!isEditable}
            />
            <FormikInput
              name="workAddressZip"
              type="text"
              id="workAddressZip"
              label="Zip"
              disabled={!isEditable}
            />
          </div>
          {isEditable && (
            <div className="flex md:col-span-3 flex-row gap-4 justify-end">
              <Button
                variant={"secondary"}
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
